import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, getCurrentUserProfile } from '../lib/supabase';
import type { UserProfile } from '../types';
import { isExecutiveAccount } from '../lib/userValidation';
import { userStore } from '../lib/userStore';
import { initSessionUsageTracker } from '../lib/activityLogger';

interface AuthContextType {
  user: any;
  profile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isExecutive: boolean;
  isManager: boolean;
  isSalesRep: boolean;
  isAccountant: boolean;
  isMarketing: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const recordLoginAudit = (userRecord: { id: string; email: string; name?: string; role?: string }) => {
    try {
      const auditPayload = {
        user_id: userRecord.id,
        user_email: userRecord.email,
        user_name: userRecord.name || userRecord.email.split('@')[0],
        role: userRecord.role || 'sales_rep',
        event_type: 'login',
        ip_address: '127.0.0.1 (Client)',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
        login_time: new Date().toISOString(),
      };

      supabase.from('login_audit_logs').insert([auditPayload]).then(({ error }) => {
        if (error) {
          console.info('Audit log note:', error.message);
        }
      }, () => {});
    } catch (err) {
      console.warn('Audit record warning:', err);
    }
  };

  useEffect(() => {
    // 1. Check userStore active session first
    const activeStoredSession = userStore.getActiveSession();
    if (activeStoredSession?.user && activeStoredSession?.profile) {
      if (activeStoredSession.profile.is_active !== false) {
        setUser(activeStoredSession.user);
        setProfile(activeStoredSession.profile);
        setLoading(false);
      }
    }

    // 2. Check Supabase auth session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const userProfile = await getCurrentUserProfile();
        if (userProfile && userProfile.is_active === false) {
          await supabase.auth.signOut();
          userStore.setActiveSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        setUser(session.user);
        setProfile(userProfile);
        userStore.setActiveSession({ user: session.user, profile: userProfile! });
        recordLoginAudit({
          id: session.user.id,
          email: session.user.email || '',
          name: userProfile?.name,
          role: userProfile?.role,
        });
        initSessionUsageTracker({
          email: session.user.email || '',
          name: userProfile?.name,
          role: userProfile?.role,
        });
      } else if (!activeStoredSession) {
        setUser(null);
        setProfile(null);
      }
      
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        // Bridged local account: the JWT arrived from the background sign-in.
        // Keep the stored profile/identity (store id, discount limits, name) —
        // only the cloud session was missing.
        const stored = userStore.getActiveSession();
        const storedEmail = (stored?.profile?.email || '').toLowerCase();
        const sessionEmail = (session.user.email || '').toLowerCase();
        if (stored?.profile && storedEmail && storedEmail === sessionEmail) {
          setUser(stored.user);
          setProfile(stored.profile);
          setLoading(false);
          return;
        }
        const userProfile = await getCurrentUserProfile();
        if (userProfile && userProfile.is_active === false) {
          await supabase.auth.signOut();
          userStore.setActiveSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        setUser(session.user);
        setProfile(userProfile);
        userStore.setActiveSession({ user: session.user, profile: userProfile! });
        recordLoginAudit({
          id: session.user.id,
          email: session.user.email || '',
          name: userProfile?.name,
          role: userProfile?.role,
        });
      } else {
        const active = userStore.getActiveSession();
        if (!active) {
          setUser(null);
          setProfile(null);
        }
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const trimmedEmail = email.trim().toLowerCase();

    // 1. First authenticate with userStore for enterprise accounts
    try {
      const storeAuth = userStore.authenticate(trimmedEmail, password);
      if (storeAuth) {
        if (storeAuth.profile.is_active === false) {
          userStore.setActiveSession(null);
          setUser(null);
          setProfile(null);
          throw new Error('Your account has been deactivated. Please contact your system administrator.');
        }
        userStore.setActiveSession(storeAuth);
        setUser(storeAuth.user);
        setProfile(storeAuth.profile);
        recordLoginAudit({
          id: storeAuth.user.id,
          email: storeAuth.user.email || '',
          name: storeAuth.profile.name,
          role: storeAuth.profile.role,
        });
        // Bridge: also establish a Supabase Auth session (JWT) for locally
        // provisioned staff. Hardened RLS policies require an authenticated
        // role, so without this bridge local accounts would see empty data.
        // NON-FATAL: if no cloud credential exists yet the user still signs in
        // (data will stay blank until the cloud account is created — see
        // RUNBOOK_APPLY_RLS.md §5).
        supabase.auth
          .signInWithPassword({ email: trimmedEmail, password })
          .catch(() => {
            /* no cloud account for this email — local session remains valid */
          });
        return;
      }
    } catch (storeErr: any) {
      if (storeErr.message.includes('deactivated')) {
        throw storeErr;
      }
      // If it's invalid password, throw right away
      if (storeErr.message.includes('Invalid login credentials')) {
        throw storeErr;
      }
    }

    // 2. Otherwise authenticate with Supabase Auth
    let authUser: any = null;
    const { data, error } = await supabase.auth.signInWithPassword({ email: trimmedEmail, password });
    
    if (!error && data?.user) {
      authUser = data.user;
    } else if (error) {
      throw error;
    }

    if (authUser) {
      const dbProfile = await getCurrentUserProfile();
      const userProfile = dbProfile || {
        id: authUser.id,
        email: authUser.email || trimmedEmail,
        name: authUser.user_metadata?.name || trimmedEmail.split('@')[0],
        role: authUser.user_metadata?.role || (trimmedEmail.includes('admin') ? 'admin' : 'sales_rep'),
        is_active: true,
        can_approve_discounts: trimmedEmail.includes('admin') || trimmedEmail.includes('soly') || trimmedEmail.includes('manager'),
        max_discount_limit: trimmedEmail.includes('admin') || trimmedEmail.includes('soly') ? 100 : (trimmedEmail.includes('manager') ? 20 : 10),
      };

      if (userProfile && userProfile.is_active === false) {
        await supabase.auth.signOut();
        userStore.setActiveSession(null);
        setUser(null);
        setProfile(null);
        throw new Error('Your account has been deactivated. Please contact your system administrator.');
      }
      userStore.setActiveSession({ user: authUser, profile: userProfile });
      setUser(authUser);
      setProfile(userProfile);
      recordLoginAudit({
        id: authUser.id,
        email: authUser.email || '',
        name: userProfile?.name,
        role: userProfile?.role,
      });
    }
  };

  const signOut = async () => {
    userStore.setActiveSession(null);
    setUser(null);
    setProfile(null);
    try {
      await supabase.auth.signOut();
    } catch (e) {}
  };

  const isExecutiveUser = isExecutiveAccount(profile, user?.email);

  const value = {
    user,
    profile,
    loading,
    signIn,
    signOut,
    isAdmin: profile?.role === 'admin',
    isExecutive: Boolean(isExecutiveUser),
    isManager: profile?.role === 'manager',
    isSalesRep: profile?.role === 'sales_rep',
    isAccountant: profile?.role === 'accountant',
    isMarketing: profile?.role === 'marketing',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    return {
      user: null,
      profile: null,
      loading: false,
      signIn: async () => {},
      signOut: async () => {},
      isAdmin: false,
      isExecutive: false,
      isManager: false,
      isSalesRep: false,
      isAccountant: false,
      isMarketing: false,
    };
  }
  return context;
}

